import React, { createContext, useContext, useState, useCallback } from 'react';

// ================================================
// LAB DEMO CONTEXT
// Manages demo items created from LAB conversations
// ================================================

const LabDemoContext = createContext(null);

// Demo conversion date
const DEMO_CONVERSION_DATE = new Date().toLocaleDateString('en-US', { 
  month: 'long', 
  day: 'numeric', 
  year: 'numeric' 
});

// LAB-created project data
const LAB_PROJECT = {
  id: 'lab-demo-project-1',
  name: 'Q3 Regional Expansion Strategy',
  description: 'Phased regional expansion into Pacific Northwest and Southwest markets. Strategic plan developed in THE LAB with AI-assisted analysis.',
  project_lead: 'tm1',
  status: 'on-track',
  created_at: new Date().toISOString(),
  completion_percentage: 0,
  active_tasks: 8,
  overdue_steps: 0,
  overdue_phases: 0,
  on_time_rate: 1.0,
  isLabGenerated: true,
  labConversationId: 'demo-q3-growth',
  labConversionDate: DEMO_CONVERSION_DATE,
  phases: [
    { name: 'Pre-Launch Preparation', weeks: '1-6', status: 'Planned' },
    { name: 'Pacific Northwest Market Entry', weeks: '7-14', status: 'Planned' },
    { name: 'Southwest Market Entry', weeks: '15-24', status: 'Planned' }
  ],
  owner: {
    id: 'tm1',
    name: 'Sarah Chen',
    avatar: 'SC',
    title: 'Head of Product'
  },
  contributors: [
    { id: 'tm2', name: 'Marcus Rivera', avatar: 'MR' },
    { id: 'tm4', name: 'David Park', avatar: 'DP' },
    { id: 'tm5', name: 'Aisha Patel', avatar: 'AP' }
  ]
};

// LAB-created tasks data
const LAB_TASKS = [
  { 
    id: 'lab-task-1',
    project_id: 'lab-demo-project-1',
    title: 'Conduct regional market analysis for both markets', 
    description: 'Comprehensive market analysis covering competitive landscape, addressable market size, and growth potential in Pacific Northwest and Southwest regions.',
    primary_lane: 'strategy', 
    required_level: 'L3', 
    supporting_lanes: ['vision', 'marketing'],
    status: 'Planned',
    due_date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Complete market reports for both regions with competitive analysis and opportunity assessment',
    owner_id: 'tm1',
    contributors: ['tm5'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 1
  },
  { 
    id: 'lab-task-2',
    project_id: 'lab-demo-project-1',
    title: 'Identify and brief Operations L3 Lead for Pacific Northwest', 
    description: 'Recruit or designate an Operations L3 Lead who will serve as the operational anchor for the Pacific Northwest region expansion.',
    primary_lane: 'operations', 
    required_level: 'L4', 
    supporting_lanes: ['relationships'],
    status: 'Planned',
    due_date: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'L3 Operations Lead identified, briefed, and ready for deployment',
    owner_id: 'tm4',
    contributors: ['tm1'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 1
  },
  { 
    id: 'lab-task-3',
    project_id: 'lab-demo-project-1',
    title: 'Develop 6-week pre-launch marketing campaign for Pacific Northwest', 
    description: 'Create comprehensive marketing campaign to build brand awareness in Pacific Northwest before market entry.',
    primary_lane: 'marketing', 
    required_level: 'L3',
    supporting_lanes: ['creative', 'strategy'],
    status: 'Planned',
    due_date: new Date(Date.now() + 28 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Complete campaign strategy, creative assets, and media plan ready for execution',
    owner_id: 'tm3',
    contributors: ['tm5', 'tm1'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 1
  },
  { 
    id: 'lab-task-4',
    project_id: 'lab-demo-project-1',
    title: 'Create regional team deployment plan', 
    description: 'Develop comprehensive plan for team deployment including hiring timeline, relocation support, and onboarding procedures.',
    primary_lane: 'operations', 
    required_level: 'L3',
    supporting_lanes: ['culture'],
    status: 'Planned',
    due_date: new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Deployment plan with clear milestones, budget, and resource allocation',
    owner_id: 'tm4',
    contributors: ['tm2'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 1
  },
  { 
    id: 'lab-task-5',
    project_id: 'lab-demo-project-1',
    title: 'Set up regional infrastructure and logistics (Pacific Northwest)', 
    description: 'Establish physical presence, local vendor relationships, and operational infrastructure in Pacific Northwest.',
    primary_lane: 'operations', 
    required_level: 'L2',
    supporting_lanes: ['finance'],
    status: 'Planned',
    due_date: new Date(Date.now() + 49 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Office space secured, vendors contracted, tech infrastructure operational',
    owner_id: 'tm4',
    contributors: ['tm6'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 2
  },
  { 
    id: 'lab-task-6',
    project_id: 'lab-demo-project-1',
    title: 'Launch Pacific Northwest market entry', 
    description: 'Execute full market entry including team deployment, client acquisition, and operational launch.',
    primary_lane: 'revenue', 
    required_level: 'L4',
    supporting_lanes: ['marketing', 'operations', 'relationships'],
    status: 'Planned',
    due_date: new Date(Date.now() + 63 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Successful launch with 3+ initial clients signed within first 30 days',
    owner_id: 'tm1',
    contributors: ['tm4', 'tm5', 'tm3'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 2
  },
  { 
    id: 'lab-task-7',
    project_id: 'lab-demo-project-1',
    title: 'Evaluate Pacific Northwest performance before Southwest launch', 
    description: 'Comprehensive performance review of Pacific Northwest operations to inform Southwest market entry strategy.',
    primary_lane: 'strategy', 
    required_level: 'L4',
    supporting_lanes: ['finance', 'operations'],
    status: 'Planned',
    due_date: new Date(Date.now() + 77 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Performance report with key learnings and recommendations for Southwest expansion',
    owner_id: 'tm1',
    contributors: ['tm6', 'tm4'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 3
  },
  { 
    id: 'lab-task-8',
    project_id: 'lab-demo-project-1',
    title: 'Begin Southwest pre-launch preparation', 
    description: 'Initiate Southwest market pre-launch activities applying lessons learned from Pacific Northwest.',
    primary_lane: 'marketing', 
    required_level: 'L3',
    supporting_lanes: ['operations', 'strategy'],
    status: 'Planned',
    due_date: new Date(Date.now() + 84 * 24 * 60 * 60 * 1000).toISOString(),
    definition_of_success: 'Southwest pre-launch campaign and operational planning initiated',
    owner_id: 'tm3',
    contributors: ['tm4', 'tm5'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE,
    phase: 3
  }
];

// LAB-created focus blocks data
const LAB_FOCUS_BLOCKS = [
  { 
    id: 'lab-focus-1',
    title: 'Weekly Expansion Strategy Review', 
    duration: '60 min', 
    type: 'Recurring',
    priority: 'high',
    lane: 'strategy',
    level: 'L4',
    scheduledFor: 'Every Monday, 9:00 AM',
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  },
  { 
    id: 'lab-focus-2',
    title: 'L4 Decision Checkpoint', 
    duration: '45 min', 
    type: 'Phase Gate',
    priority: 'critical',
    lane: 'strategy',
    level: 'L4',
    scheduledFor: 'Before each phase transition',
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  },
  { 
    id: 'lab-focus-3',
    title: 'Regional Director Briefing Sessions', 
    duration: '30 min', 
    type: 'Bi-weekly',
    priority: 'medium',
    lane: 'relationships',
    level: 'L3',
    scheduledFor: 'Every other Wednesday, 2:00 PM',
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  },
  { 
    id: 'lab-focus-4',
    title: 'Board Update on Expansion Progress', 
    duration: '60 min', 
    type: 'Monthly',
    priority: 'high',
    lane: 'vision',
    level: 'L4',
    scheduledFor: 'Last Friday of each month',
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  }
];

// LAB-created team role recommendations
const LAB_TEAM_ROLES = [
  { 
    id: 'lab-role-1',
    title: 'Operations L3 Lead — Pacific Northwest', 
    level: 'L3', 
    lane: 'operations', 
    deadline: 'Week 4',
    status: 'Open Position',
    description: 'Operational anchor for Pacific Northwest regional expansion. Will manage daily execution and serve as primary point of contact for regional operations.',
    requirements: ['5+ years operations experience', 'Team leadership experience', 'Regional market knowledge preferred'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  },
  { 
    id: 'lab-role-2',
    title: 'Operations L3 Lead — Southwest', 
    level: 'L3', 
    lane: 'operations', 
    deadline: 'Week 12',
    status: 'Open Position',
    description: 'Operational anchor for Southwest regional expansion. Will apply learnings from Pacific Northwest launch.',
    requirements: ['5+ years operations experience', 'Team leadership experience', 'Southwest market knowledge preferred'],
    isLabGenerated: true,
    labConversationId: 'demo-q3-growth',
    labConversionDate: DEMO_CONVERSION_DATE
  }
];

export const LabDemoProvider = ({ children }) => {
  const [demoCompleted, setDemoCompleted] = useState(false);
  const [conversionDate, setConversionDate] = useState(null);

  // Complete the demo conversion
  const completeDemo = useCallback(() => {
    setDemoCompleted(true);
    setConversionDate(DEMO_CONVERSION_DATE);
  }, []);

  // Reset the demo
  const resetDemo = useCallback(() => {
    setDemoCompleted(false);
    setConversionDate(null);
  }, []);

  // Get LAB-created items for various pages
  const getLabProject = useCallback(() => {
    return demoCompleted ? LAB_PROJECT : null;
  }, [demoCompleted]);

  const getLabTasks = useCallback(() => {
    return demoCompleted ? LAB_TASKS : [];
  }, [demoCompleted]);

  const getLabFocusBlocks = useCallback(() => {
    return demoCompleted ? LAB_FOCUS_BLOCKS : [];
  }, [demoCompleted]);

  const getLabTeamRoles = useCallback(() => {
    return demoCompleted ? LAB_TEAM_ROLES : [];
  }, [demoCompleted]);

  const value = {
    demoCompleted,
    conversionDate,
    completeDemo,
    resetDemo,
    getLabProject,
    getLabTasks,
    getLabFocusBlocks,
    getLabTeamRoles,
    // Direct access to data for summary views
    labProject: demoCompleted ? LAB_PROJECT : null,
    labTasks: demoCompleted ? LAB_TASKS : [],
    labFocusBlocks: demoCompleted ? LAB_FOCUS_BLOCKS : [],
    labTeamRoles: demoCompleted ? LAB_TEAM_ROLES : [],
    // Stats for success screen
    totalItemsCreated: LAB_PROJECT ? 1 + LAB_TASKS.length + LAB_FOCUS_BLOCKS.length + LAB_TEAM_ROLES.length : 0
  };

  return (
    <LabDemoContext.Provider value={value}>
      {children}
    </LabDemoContext.Provider>
  );
};

export const useLabDemo = () => {
  const context = useContext(LabDemoContext);
  if (!context) {
    throw new Error('useLabDemo must be used within a LabDemoProvider');
  }
  return context;
};

export default LabDemoContext;
